import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { formatMinor, pickDisplay } from "@/lib/CurrencyFormatter";

/**
 * Iter XX.44.A — Partner Wallet page (Stripe-quality UX).
 * Iter XX.48   — Country-aware currency + payout method restriction.
 *
 * Backend ledger stays in INR paise (canonical). Presentation only:
 *  • The wallet API attaches a `display` block with FX-converted
 *    minor amounts + metadata (provider, staleness, rate).
 *  • Payout methods are filtered by the partner's country using the
 *    `available_payout_methods` list from the same endpoint.
 *  • `CurrencyFormatter.formatMinor` handles locale-aware rendering.
 *  • If FX rates are unavailable we fall back to the canonical INR
 *    ledger currency rather than showing an inaccurate conversion.
 */
const METHOD_SPECS = {
  upi:     { label: "UPI",                  fields: [{ key: "upi_id", label: "UPI ID (e.g. name@bank)" }, { key: "label", label: "Nickname (optional)", optional: true }] },
  bank:    { label: "Bank transfer",        fields: [{ key: "beneficiary_name", label: "Beneficiary name" }, { key: "account_number", label: "Account number" }, { key: "ifsc_or_swift", label: "IFSC or SWIFT" }, { key: "label", label: "Nickname (optional)", optional: true }] },
  wise:    { label: "Wise",                 fields: [{ key: "email", label: "Wise account email" }, { key: "label", label: "Nickname (optional)", optional: true }] },
  paypal:  { label: "PayPal",               fields: [{ key: "email", label: "PayPal email" }, { key: "label", label: "Nickname (optional)", optional: true }] },
  interac: { label: "Interac e-Transfer",   fields: [{ key: "email", label: "Interac email" }, { key: "label", label: "Nickname (optional)", optional: true }] },
  other:   { label: "Other (founder-approved)", fields: [{ key: "details", label: "Describe your preferred method" }, { key: "label", label: "Nickname (optional)", optional: true }] },
};

export default function PartnerWallet() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [statement, setStatement] = useState([]);
  const [statementCcy, setStatementCcy] = useState("INR");
  const [loading, setLoading] = useState(true);

  const [showAddMethod, setShowAddMethod] = useState(false);
  const [methodType, setMethodType] = useState("upi");
  const [methodCountry, setMethodCountry] = useState("");
  const [methodFields, setMethodFields] = useState({});
  const [saving, setSaving] = useState(false);

  const [showCashOut, setShowCashOut] = useState(false);
  const [cashOutMethodId, setCashOutMethodId] = useState(null);

  const display = useMemo(() => pickDisplay(wallet), [wallet]);
  const partnerCountry = wallet?.country || null;
  const availableMethods = useMemo(() => {
    const list = wallet?.available_payout_methods || Object.keys(METHOD_SPECS);
    return list.filter(m => METHOD_SPECS[m]);
  }, [wallet]);

  // Currency options for display block.
  const fmt = useCallback((minor) => formatMinor(minor, { currency: display.currency }), [display.currency]);
  const canonicalFmt = useCallback((minor) => formatMinor(minor, { currency: display.canonical }), [display.canonical]);

  const load = useCallback(async () => {
    try {
      const [wRes, pRes, sRes] = await Promise.all([
        api.get("/partners/me/wallet"),
        api.get("/partners/me/wallet/payouts"),
        api.get("/partners/me/wallet/statement"),
      ]);
      setWallet(wRes.data);
      setPayouts(pRes.data.payouts || []);
      setStatement(sRes.data.statement || []);
      setStatementCcy(sRes.data.display_currency || "INR");
      // Default country picker to partner country when empty.
      if (!methodCountry) setMethodCountry((wRes.data.country || "IN"));
      // Default method to the first allowed for the partner.
      const allowed = (wRes.data.available_payout_methods || []).filter(m => METHOD_SPECS[m]);
      if (allowed.length && !allowed.includes(methodType)) setMethodType(allowed[0]);
    } catch (e) {
      if (e?.response?.status === 404) { navigate("/partner/claim"); return; }
      toast.error("Couldn't load wallet");
    } finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/login?return_to=/partner/wallet"); return; }
    load();
  }, [authLoading, user, load, navigate]);

  async function addMethod() {
    setSaving(true);
    try {
      const details = { ...methodFields };
      await api.post("/partners/me/wallet/methods", {
        method: methodType,
        country: methodCountry || null,
        details,
      });
      toast.success("Payout method saved");
      setShowAddMethod(false);
      setMethodFields({});
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save method"); }
    finally { setSaving(false); }
  }

  async function submitCashOut() {
    if (!cashOutMethodId) { toast.error("Pick a payout method"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/partners/me/wallet/cash-out", {
        method_id: cashOutMethodId,
      });
      // API returns amount_minor in canonical currency.
      toast.success(`Cash-out requested: ${canonicalFmt(data.payout.amount_minor)}`);
      setShowCashOut(false);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't submit request"); }
    finally { setSaving(false); }
  }

  async function cancelCashOut(payoutId) {
    if (!window.confirm("Cancel this cash-out request? The balance will be returned to Available.")) return;
    try {
      await api.post(`/partners/me/wallet/cash-out/${payoutId}/cancel`);
      toast.success("Cancelled");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't cancel"); }
  }

  if (authLoading || loading) return <div className="min-h-screen bg-neutral-950 text-neutral-100 flex items-center justify-center">Loading wallet…</div>;
  if (!wallet) return <div className="min-h-screen bg-neutral-950 text-neutral-100 flex items-center justify-center">No wallet yet.</div>;

  const w = wallet.wallet || {};
  const cashOutEnabled = wallet.cash_out_enabled && Number(w.available) > 0;
  const currentMethodSpec = METHOD_SPECS[methodType] || METHOD_SPECS.other;
  const amounts = display.amounts || {};

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100" data-testid="partner-wallet-page">
      <div className="max-w-3xl mx-auto px-6 py-10">
        <Link to="/partner" className="text-sm text-neutral-500 hover:text-neutral-300" data-testid="wallet-back">← Dashboard</Link>
        <div className="flex items-center justify-between mt-2 mb-2">
          <h1 className="text-3xl font-semibold">Wallet</h1>
          <div
            className="text-xs px-2 py-1 rounded-full border border-neutral-800 bg-neutral-900 text-neutral-400 uppercase tracking-wide"
            data-testid="wallet-currency-badge"
            title={display.converted ? `Converted from ${display.canonical} via ${display.provider || "FX"} on ${display.fetchedAt || "recent"}` : "Ledger currency"}
          >
            {display.currency}
            {display.converted && (
              <span className="ml-1 normal-case text-neutral-500">· display</span>
            )}
          </div>
        </div>
        {display.converted && (
          <div className="text-xs text-neutral-500 mb-6" data-testid="wallet-fx-note">
            Amounts converted from {display.canonical} to {display.currency}
            {display.fxRate ? ` at ${display.fxRate.toFixed(4)}` : ""}
            {display.fxStale ? " · rates may be a day old" : ""}. Ledger stays in {display.canonical}.
          </div>
        )}
        {!display.converted && display.reason === "fx_rates_unavailable" && (
          <div className="text-xs text-amber-400 mb-6" data-testid="wallet-fx-fallback">
            Live exchange rates unavailable — showing your balance in {display.canonical}. Try again later.
          </div>
        )}
        {!display.converted && display.currency === display.canonical && !display.reason && (
          <div className="text-xs text-neutral-600 mb-6" data-testid="wallet-fx-same">
            Displayed in your ledger currency.
          </div>
        )}

        {/* Balances */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="wallet-balances">
          <Balance label="Available" value={fmt(amounts.available)} highlight testid="wallet-available" />
          <Balance label="Pending" value={fmt(amounts.pending)} testid="wallet-pending" />
          <Balance label="Being paid out" value={fmt(amounts.payout_reserved)} testid="wallet-reserved" />
          <Balance label="Paid lifetime" value={fmt(amounts.paid_lifetime)} testid="wallet-paid" />
        </div>

        {/* Cash Out button */}
        <div className="bg-gradient-to-br from-emerald-950/40 to-neutral-900 border border-emerald-800/30 rounded-xl p-6 mb-6 flex items-center justify-between" data-testid="wallet-cashout-cta">
          <div>
            <div className="text-sm text-neutral-400">Ready to cash out?</div>
            <div className="text-xs text-neutral-500 mt-1">
              Minimum: {canonicalFmt(wallet.minimum_cash_out_minor ?? 50000)}
              {display.converted && (
                <span className="ml-1">
                  (≈ {fmt(Math.round((wallet.minimum_cash_out_minor ?? 50000) * (display.fxRate || 1)))})
                </span>
              )}
            </div>
          </div>
          <Button
            onClick={() => setShowCashOut(true)}
            disabled={!cashOutEnabled}
            className="bg-emerald-500 hover:bg-emerald-400 text-neutral-950 disabled:opacity-40"
            data-testid="wallet-cashout-btn"
          >Cash Out</Button>
        </div>

        {/* Payout methods */}
        <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-6 mb-6" data-testid="wallet-payout-methods">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-base font-medium">Payout methods</h2>
            <Button variant="outline" size="sm" onClick={() => setShowAddMethod(v => !v)} className="border-neutral-700" data-testid="wallet-add-method-toggle">
              {showAddMethod ? "Cancel" : "+ Add method"}
            </Button>
          </div>
          {partnerCountry && (
            <div className="text-xs text-neutral-500 mb-3" data-testid="wallet-methods-country-hint">
              Payout options shown for {partnerCountry}: {availableMethods.map(m => METHOD_SPECS[m].label).join(" · ")}
            </div>
          )}
          {(wallet.methods || []).length === 0 && !showAddMethod && (
            <div className="text-sm text-neutral-500">No payout methods yet. Add one before requesting a cash-out.</div>
          )}
          <div className="space-y-2 mb-4">
            {(wallet.methods || []).map(m => (
              <div key={m.method_id} className="flex justify-between items-center text-sm py-2 border-b border-neutral-800 last:border-0" data-testid={`method-row-${m.method}`}>
                <div>
                  <div className="text-neutral-200 capitalize">{m.details?.label || METHOD_SPECS[m.method]?.label || m.method}</div>
                  <div className="text-xs text-neutral-500 flex gap-2 mt-0.5">
                    <span className="uppercase">{m.method}</span>
                    {m.country && <span>· {m.country}</span>}
                    {m.details?.upi_id && <span>· {m.details.upi_id}</span>}
                    {m.details?.email && <span>· {m.details.email}</span>}
                    {m.details?.account_number && <span>· {m.details.account_number}</span>}
                  </div>
                </div>
                {m.is_default && <span className="text-xs text-emerald-400">Default</span>}
              </div>
            ))}
          </div>

          {showAddMethod && (
            <div className="bg-neutral-950 border border-neutral-800 rounded p-4 space-y-3" data-testid="wallet-add-method-form">
              <div>
                <label className="text-xs text-neutral-400 block mb-1">Payout method</label>
                <select value={methodType} onChange={(e) => { setMethodType(e.target.value); setMethodFields({}); }}
                        className="w-full px-2 py-1.5 bg-neutral-900 border border-neutral-800 rounded text-sm"
                        data-testid="method-type-select">
                  {availableMethods.map(m => <option key={m} value={m}>{METHOD_SPECS[m].label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-neutral-400 block mb-1">Country (ISO code, e.g. IN, CA, GB)</label>
                <Input value={methodCountry} onChange={(e) => setMethodCountry(e.target.value.toUpperCase().slice(0, 2))}
                       className="bg-neutral-900 border-neutral-800 text-sm" data-testid="method-country-input" maxLength={2} />
              </div>
              {currentMethodSpec.fields.map(f => (
                <div key={f.key}>
                  <label className="text-xs text-neutral-400 block mb-1">{f.label}{!f.optional && " *"}</label>
                  <Input
                    value={methodFields[f.key] || ""}
                    onChange={(e) => setMethodFields({ ...methodFields, [f.key]: e.target.value })}
                    className="bg-neutral-900 border-neutral-800 text-sm"
                    data-testid={`method-field-${f.key}`}
                  />
                </div>
              ))}
              <Button onClick={addMethod} disabled={saving}
                      className="bg-emerald-500 hover:bg-emerald-400 text-neutral-950 w-full disabled:opacity-40"
                      data-testid="method-save-btn">
                {saving ? "Saving…" : "Save payout method"}
              </Button>
              <div className="text-xs text-neutral-500">
                We only ask for what your chosen method needs. Nothing is shared with players.
              </div>
            </div>
          )}
        </div>

        {/* Cash-Out modal (inline) */}
        {showCashOut && (
          <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-6 mb-6" data-testid="wallet-cashout-modal">
            <h2 className="text-base font-medium mb-4">Request cash-out</h2>
            <div className="text-sm text-neutral-400 mb-2">Cashing out full available balance:</div>
            <div className="text-3xl font-semibold text-emerald-300 mb-1" data-testid="cashout-amount">
              {canonicalFmt(amounts.available && display.converted ? Math.round((amounts.available) / (display.fxRate || 1)) : (w.available_minor ?? Math.round(Number(w.available ?? 0) * 100)))}
            </div>
            {display.converted && (
              <div className="text-xs text-neutral-500 mb-4" data-testid="cashout-amount-local">
                ≈ {fmt(amounts.available)} at {display.fxRate?.toFixed?.(4)} {display.currency}/{display.canonical}
              </div>
            )}
            <label className="text-xs text-neutral-400 block mb-2 mt-2">Send to</label>
            <div className="space-y-2 mb-4">
              {(wallet.methods || []).map(m => (
                <label key={m.method_id} className={`flex items-center gap-2 border rounded p-3 cursor-pointer ${cashOutMethodId === m.method_id ? "border-emerald-500 bg-emerald-950/30" : "border-neutral-800"}`} data-testid={`cashout-method-${m.method}`}>
                  <input type="radio" name="cashout-method" checked={cashOutMethodId === m.method_id}
                         onChange={() => setCashOutMethodId(m.method_id)} className="accent-emerald-500" />
                  <div className="flex-1 text-sm">
                    <div className="text-neutral-200 capitalize">{m.details?.label || METHOD_SPECS[m.method]?.label || m.method}</div>
                    <div className="text-xs text-neutral-500 uppercase">{m.method} {m.country ? `· ${m.country}` : ""}</div>
                  </div>
                </label>
              ))}
              {(wallet.methods || []).length === 0 && (
                <div className="text-sm text-neutral-500">Add a payout method first.</div>
              )}
            </div>
            <div className="flex gap-2">
              <Button onClick={submitCashOut} disabled={saving || !cashOutMethodId}
                      className="bg-emerald-500 hover:bg-emerald-400 text-neutral-950 disabled:opacity-40"
                      data-testid="cashout-submit">
                {saving ? "Submitting…" : "Submit request"}
              </Button>
              <Button variant="outline" onClick={() => setShowCashOut(false)} className="border-neutral-700"
                      data-testid="cashout-cancel">Cancel</Button>
            </div>
            <div className="text-xs text-neutral-500 mt-4">
              {"We'll send the funds within 5 business days and update your wallet with the payment reference."}
            </div>
          </div>
        )}

        {/* Payout history */}
        {payouts.length > 0 && (
          <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-6 mb-6" data-testid="wallet-payout-history">
            <h2 className="text-base font-medium mb-4">Payout history</h2>
            <div className="space-y-2">
              {payouts.map(p => (
                <div key={p.payout_id} className="flex justify-between items-center text-sm py-2 border-b border-neutral-800 last:border-0" data-testid={`payout-row-${p.status}`}>
                  <div>
                    <div className="text-neutral-200">{canonicalFmt(p.amount_minor)}</div>
                    <div className="text-xs text-neutral-500 mt-0.5">
                      {(METHOD_SPECS[p.method]?.label || p.method?.toUpperCase())} · {new Date(p.created_at).toLocaleDateString()}
                      {p.payment_reference && <span> · Ref {p.payment_reference}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={p.status} />
                    {(p.status === "requested" || p.status === "processing") && (
                      <button onClick={() => cancelCashOut(p.payout_id)}
                              className="text-xs text-neutral-500 hover:text-red-400"
                              data-testid={`payout-cancel-${p.payout_id}`}>
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Statement */}
        <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-6 mb-6" data-testid="wallet-statement">
          <h2 className="text-base font-medium mb-4">Statement</h2>
          {statement.length === 0 ? (
            <div className="text-sm text-neutral-500">No ledger events yet. When a referred player upgrades to Gully Plus, an accrual will appear here.</div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {statement.map(row => {
                const dispCcy = row.display_currency || statementCcy;
                const dispMinor = row.display_amount_minor ?? row.amount_minor;
                return (
                  <div key={row.event_id} className="flex justify-between items-center text-sm py-2 border-b border-neutral-800 last:border-0" data-testid={`statement-row-${row.event_type}`}>
                    <div>
                      <div className="text-neutral-200">{row.description}</div>
                      <div className="text-xs text-neutral-500 mt-0.5">{new Date(row.happened_at).toLocaleString()}</div>
                    </div>
                    <div className={`text-sm ${row.amount >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                      {row.amount >= 0 ? "+" : "-"}
                      {formatMinor(Math.abs(dispMinor || 0), { currency: dispCcy })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="text-xs text-neutral-500 mt-8">
          The ledger is immutable — corrections, refunds, chargebacks and payouts appear as new entries, never edits. Referred players are always shown anonymously.
        </div>
      </div>
    </div>
  );
}

function Balance({ label, value, testid, highlight = false }) {
  return (
    <div className={`rounded-lg p-4 border ${highlight ? "bg-emerald-950/30 border-emerald-800/40" : "bg-neutral-900 border-neutral-800"}`} data-testid={testid}>
      <div className={`text-2xl font-semibold ${highlight ? "text-emerald-300" : "text-neutral-200"}`}>{value}</div>
      <div className="text-xs text-neutral-500 mt-1">{label}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const cls = status === "paid" ? "bg-emerald-500/10 text-emerald-400" :
              status === "processing" ? "bg-blue-500/10 text-blue-400" :
              status === "requested" ? "bg-amber-500/10 text-amber-400" :
              status === "failed" ? "bg-red-500/10 text-red-400" :
              "bg-neutral-800 text-neutral-400";
  return <span className={`text-xs px-2 py-0.5 rounded-full ${cls}`}>{status}</span>;
}
